import compact from "lodash/compact";
import orderBy from "lodash/orderBy";
import { copyTextToClipboard } from "PFCore/helpers/copy_text_to_clipboard";
import { isMsTeams, openChatInMsTeams } from "PFCore/helpers/ms_teams";
import { isProfileFieldPermitted } from "PFCore/helpers/profile_permissions";
import { isFeatureEnabled } from "PFCore/helpers/use_is_feature_enabled";
import { useCurrentAccount } from "PFCore/hooks/queries/account";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { FeatureFlag } from "PFCore/types/feature_flag";
import { IconName } from "PFTheme/graphics/icons";
import { AccessLevel, Profile } from "PFTypes";
import { useTranslation } from "react-i18next";

import { CONTACT_TYPE_TO_ICON_MAP } from "../../constants/contact_type_icon_map";

export type ContactInfo = {
  value: string;
  iconName: IconName;
  type: string;
  title: string;
  url?: string;
  target?: "_blank" | "_self";
  onClick?: VoidFunction;
};

const PHONE_NUMBER_REGEX = /\+?[\d\s-]*\d/;
const TYPES_GUARDED_BY_FEATURE_FLAG: FeatureFlag[] = [
  FeatureFlag.Facebook,
  FeatureFlag.Twitter,
  FeatureFlag.Instagram,
  FeatureFlag.Website
];

const handleCopyContactNumber = (value: string) => {
  if (value) {
    const match = PHONE_NUMBER_REGEX.exec(value);
    const number = match ? match[0] : "";
    copyTextToClipboard(number);
  }
};

export const useContactInfoArray = (profile: Profile): ContactInfo[] => {
  const { t } = useTranslation("profiles", { keyPrefix: "contactInformation" });
  const { data: currentProfile } = useCurrentProfile();
  const { data: currentAccount } = useCurrentAccount();
  const isEnabled = isFeatureEnabled(currentAccount);

  const isContactInfoPermitted = isProfileFieldPermitted(
    currentProfile.permissionsGroup,
    profile?.permissionsGroup?.id,
    "contactInfo",
    AccessLevel.ReadOnly
  );

  const typeToAriaLabelGetterMap = {
    email: (value) => t("sendEmailTo", { value }),
    phone: (value) => t("copyPhoneNumber", { value }),
    twitter: (value) => t("twitterAccount", { value }),
    instagram: (value) => t("instagramAccount", { value }),
    facebook: (value) => t("facebookAccount", { value }),
    website: (value) => t("visitWebsite", { value }),
    ms_teams: (value) => t("messageUserInMSTeams", { value })
  };

  const emailContact: ContactInfo | null = profile.email
    ? {
        type: "email",
        value: profile.email,
        url: `mailto:${profile.email}`,
        target: "_self",
        iconName: CONTACT_TYPE_TO_ICON_MAP.email,
        title: typeToAriaLabelGetterMap.email(profile.email)
      }
    : null;

  const profileLinks = (isContactInfoPermitted ? profile.contactInformation || [] : [])
    .filter(
      ({ type }) =>
        !TYPES_GUARDED_BY_FEATURE_FLAG.includes(type as FeatureFlag) || isEnabled(type as FeatureFlag)
    )
    .map<ContactInfo>(({ type, ...contact }) => {
      let onClick: VoidFunction | undefined;
      let { value, url } = contact;

      if (type === "ms_teams" && isMsTeams()) {
        value = t("messageInMSTeams");
        const userName = contact.value;
        onClick = () => openChatInMsTeams(userName);
        url = undefined;
      } else if (type === "phone") {
        onClick = () => handleCopyContactNumber(value);

        if (contact.label) {
          value = `${contact.value} ${contact.label}`;
        }
      } else if (type === "website") {
        url = contact.url || contact.value;
        value = contact.label || contact.value;
      }

      return {
        iconName: CONTACT_TYPE_TO_ICON_MAP[type],
        type,
        value,
        target: "_blank",
        url,
        title: typeToAriaLabelGetterMap[type]?.(contact.value),
        onClick
      };
    })
    .filter(({ title, iconName }) => !!iconName && !!title);

  // Order: email, phone, other
  const contacts = orderBy(
    compact([emailContact, ...profileLinks]),
    ({ type }) => ["phone", "email"].indexOf(type),
    "desc"
  );

  return contacts;
};
